import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import baseUrl from "../api/utils";

const fmt = (n) =>
  (n || n === 0)
    ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";

const getMonthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0];
};
const getToday = () => new Date().toISOString().split("T")[0];

const SalesReportView = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const [fromDate, setFromDate] = useState(getMonthStart());
  const [toDate, setToDate] = useState(getToday());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/reports/sales`, {
        headers: { Authorization: token },
        params: { from: fromDate, to: toDate },
      });
      setReport(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/unauthorized");
        return;
      }
      console.error("Failed to load sales report", err);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchReport();
  }, []);

  const handleExportCsv = () => {
    if (!report) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [];
    lines.push(`Sales Report`);
    lines.push(`Period,${fromDate} to ${toDate}`);
    lines.push("");
    lines.push("Summary");
    lines.push(`Total Sales,${report.totalSales}`);
    lines.push(`Invoices,${report.invoiceCount}`);
    lines.push(`Cash Sales,${report.cashSales}`);
    lines.push(`Credit Sales,${report.creditSales}`);
    lines.push("");
    lines.push("Daily Sales Trend");
    lines.push("Date,Total Sales,Invoice Count");
    report.trend.forEach((r) => lines.push(`${r.date},${r.totalSales},${r.invoiceCount}`));
    lines.push("");
    lines.push("Top Items");
    lines.push("Item,Qty Sold,Revenue");
    report.topItems.forEach((r) => lines.push(`${esc(r.itemName)},${r.totalQtySold},${r.totalRevenue}`));
    lines.push("");
    lines.push("Top Customers");
    lines.push("Customer,Total Spent");
    report.topCustomers.forEach((r) => lines.push(`${esc(r.partyName)},${r.totalSpent}`));

    const csvContent = lines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sales_report_${fromDate}_to_${toDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const cellStyle = { border: "1px solid #eee", padding: "8px 10px", fontSize: "13px" };
  const thStyle = { ...cellStyle, background: "#f5f6fa", fontWeight: "600", textAlign: "left", color: "#555" };

  const maxTrendValue = report?.trend?.length
    ? Math.max(...report.trend.map((t) => t.totalSales))
    : 0;

  // 4 evenly spaced gridline levels
  const gridLevels = [1, 0.75, 0.5, 0.25, 0];

  return (
    <div style={{ background: "#f5f6fa", padding: "20px", borderRadius: "8px" }}>
      {/* ── Date range + Export ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
          <div>
            <label style={{ fontSize: "11px", color: "#888", fontWeight: "600", display: "block", marginBottom: "4px" }}>From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", background: "#fff" }} />
          </div>
          <div>
            <label style={{ fontSize: "11px", color: "#888", fontWeight: "600", display: "block", marginBottom: "4px" }}>To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", background: "#fff" }} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={fetchReport} disabled={loading}>
            {loading ? "Loading..." : "Apply"}
          </button>
        </div>
        <button className="btn btn-danger btn-sm" onClick={handleExportCsv} disabled={!report || report.trend.length === 0}>
          <i className="fa-solid fa-download mr-1"></i> Export CSV
        </button>
      </div>

      {loading && !report ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>Loading sales report...</div>
      ) : !report ? null : (
        <>
          {/* ── Summary cards — solid white, subtle shadow ── */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "24px", flexWrap: "wrap" }}>
            {[
              { label: "Total Sales", value: `₹${fmt(report.totalSales)}`, color: "#1976d2" },
              { label: "Invoices", value: report.invoiceCount, color: "#555" },
              { label: "Cash Sales", value: `₹${fmt(report.cashSales)}`, color: "#27ae60" },
              { label: "Credit Sales", value: `₹${fmt(report.creditSales)}`, color: "#c0392b" },
            ].map((card) => (
              <div key={card.label} style={{
                flex: 1, minWidth: "180px", background: "#fff",
                borderRadius: "10px", padding: "18px 20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                borderTop: `3px solid ${card.color}`,
              }}>
                <div style={{ fontSize: "11px", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {card.label}
                </div>
                <div style={{ fontSize: "24px", fontWeight: "700", color: "#222", marginTop: "6px" }}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          {/* ── Trend chart — white card, visible gridlines + Y-axis labels ── */}
          {report.trend.length > 0 && (
            <div style={{ marginBottom: "24px", background: "#fff", borderRadius: "10px", padding: "20px 24px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#333", marginBottom: "18px" }}>Daily Sales Trend</div>
              <div style={{ display: "flex" }}>
                {/* Y-axis labels */}
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "180px", paddingRight: "10px", flexShrink: 0 }}>
                  {gridLevels.map((level) => (
                    <span key={level} style={{ fontSize: "10px", color: "#aaa", textAlign: "right" }}>
                      ₹{fmt(maxTrendValue * level).split(".")[0]}
                    </span>
                  ))}
                </div>
                {/* chart area */}
                <div style={{ position: "relative", flex: 1, height: "180px" }}>
                  {/* gridlines */}
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    {gridLevels.map((level) => (
                      <div key={level} style={{ borderTop: "1px solid #eee", width: "100%" }} />
                    ))}
                  </div>
                  {/* bars */}
                  <div style={{ position: "relative", display: "flex", alignItems: "flex-end", gap: "10px", width: "100%", height: "100%", padding: "0 4px" }}>
                    {report.trend.map((point) => (
                      <div key={point.date} style={{ flex: report.trend.length < 8 ? "0 0 60px" : 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "flex-end" }}>
                        <div
                          title={`₹${fmt(point.totalSales)} · ${point.invoiceCount} invoice(s)`}
                          style={{
                            width: "100%", maxWidth: "44px",
                            height: maxTrendValue > 0 ? `${Math.max((point.totalSales / maxTrendValue) * 170, 4)}px` : "4px",
                            background: "linear-gradient(180deg, #4aa3e8, #1976d2)",
                            borderRadius: "4px 4px 0 0", cursor: "pointer", transition: "opacity 0.15s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "10px", marginLeft: "44px", justifyContent: report.trend.length < 8 ? "flex-start" : "space-between", flexWrap: "wrap" }}>
                {report.trend.map((point) => (
                  <span key={point.date} style={{ fontSize: "10px", color: "#999", flex: report.trend.length < 8 ? "0 0 54px" : 1, textAlign: "center" }}>
                    {new Date(point.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Top Items / Top Customers — white cards ── */}
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: "280px", background: "#fff", borderRadius: "10px", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#333", marginBottom: "12px" }}>Top Items</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Item</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Qty Sold</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {report.topItems.length === 0 ? (
                    <tr><td colSpan={3} style={{ ...cellStyle, textAlign: "center", color: "#aaa" }}>No data</td></tr>
                  ) : (
                    report.topItems.map((item, i) => (
                      <tr key={i}>
                        <td style={cellStyle}>{item.itemName}</td>
                        <td style={{ ...cellStyle, textAlign: "right" }}>{item.totalQtySold}</td>
                        <td style={{ ...cellStyle, textAlign: "right", fontWeight: "600" }}>₹{fmt(item.totalRevenue)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ flex: 1, minWidth: "280px", background: "#fff", borderRadius: "10px", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#333", marginBottom: "12px" }}>Top Customers</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Customer</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Total Spent</th>
                  </tr>
                </thead>
                <tbody>
                  {report.topCustomers.length === 0 ? (
                    <tr><td colSpan={2} style={{ ...cellStyle, textAlign: "center", color: "#aaa" }}>No data</td></tr>
                  ) : (
                    report.topCustomers.map((c, i) => (
                      <tr key={i}>
                        <td style={cellStyle}>{c.partyName}</td>
                        <td style={{ ...cellStyle, textAlign: "right", fontWeight: "600" }}>₹{fmt(c.totalSpent)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalesReportView;